import React from 'react';

interface EvaluationHelpProps {
  subTab?: string;
}

/**
 * Evaluation Help Content - Overview, Test Runs, Schedules
 */
const EvaluationHelp: React.FC<EvaluationHelpProps> = ({ subTab }) => {
  if (subTab === 'test-runs') {
    return <TestRunsHelp />;
  }
  if (subTab === 'schedules') {
    return <SchedulesHelp />;
  }
  return <EvaluationOverviewHelp />;
};

const EvaluationOverviewHelp: React.FC = () => (
  <>
    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Evaluation Overview
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
        The Evaluation Overview provides a summary of your prompt testing efforts, including aggregate scores, recent evaluations, and quality trends over time.
      </p>
    </div>

    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Key Metrics
      </h3>
      <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
        <p>&#8226; <span className="font-medium">Average Score</span>: Mean evaluation score across all tests</p>
        <p>&#8226; <span className="font-medium">Pass Rate</span>: Percentage of tests meeting thresholds</p>
        <p>&#8226; <span className="font-medium">Test Coverage</span>: Prompts with recent evaluations</p>
        <p>&#8226; <span className="font-medium">Trend</span>: Quality improvement or regression over time</p>
      </div>
    </div>

    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Evaluation Methods
      </h3>
      <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
        <p>&#8226; <span className="font-medium">LLM-as-Judge</span>: Use an LLM to evaluate responses</p>
        <p>&#8226; <span className="font-medium">Exact Match</span>: Compare against expected outputs</p>
        <p>&#8226; <span className="font-medium">Semantic Similarity</span>: Measure meaning similarity</p>
        <p>&#8226; <span className="font-medium">Custom Rubrics</span>: Define your own scoring criteria</p>
      </div>
    </div>

  </>
);

const TestRunsHelp: React.FC = () => (
  <>
    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Test Runs
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
        Test Runs execute evaluations against your prompts using datasets. Run tests manually or set up automated schedules to catch regressions early.
      </p>
    </div>

    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Running an Evaluation
      </h3>
      <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
        <p>1. Click <span className="font-medium">Run Evaluation</span></p>
        <p>2. Select the prompt and version to test</p>
        <p>3. Choose a dataset with test cases</p>
        <p>4. Configure scoring method and thresholds</p>
        <p>5. Run and review results</p>
      </div>
    </div>

    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Test Results
      </h3>
      <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
        <p>&#8226; <span className="font-medium">Pass/Fail</span>: Overall test status</p>
        <p>&#8226; <span className="font-medium">Score</span>: Numeric evaluation score</p>
        <p>&#8226; <span className="font-medium">Per-Item Results</span>: Detailed breakdown per test case</p>
        <p>&#8226; <span className="font-medium">Feedback</span>: Explanations from the evaluator</p>
      </div>
    </div>

    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Comparing Versions
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
        Compare test results across prompt versions to validate improvements before promoting to production.
      </p>
    </div>

  </>
);

const SchedulesHelp: React.FC = () => (
  <>
    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Evaluation Schedules
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
        Set up automated evaluation schedules to continuously monitor prompt quality. Schedules run tests periodically and alert you to regressions.
      </p>
    </div>

    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Creating a Schedule
      </h3>
      <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
        <p>&#8226; Select prompt and dataset to evaluate</p>
        <p>&#8226; Choose frequency (hourly, daily, weekly)</p>
        <p>&#8226; Set alert thresholds for failures</p>
        <p>&#8226; Configure notification channels</p>
      </div>
    </div>

    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Schedule Status
      </h3>
      <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
        <p>&#8226; <span className="font-medium">Active</span>: Schedule is running</p>
        <p>&#8226; <span className="font-medium">Paused</span>: Temporarily disabled</p>
        <p>&#8226; <span className="font-medium">Failed</span>: Recent run encountered errors</p>
      </div>
    </div>

    <div>
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-2">
        Best Practices
      </h3>
      <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
        Schedule daily evaluations for production prompts. Set up alerts to catch quality regressions before they impact users.
      </p>
    </div>

  </>
);

export default EvaluationHelp;
